import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Badge,
  Avatar,
  SimpleGrid,
  Box,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useToast,
} from '@chakra-ui/react';
import rolePermissionService from 'services/rolePermissionService';

export default function UserPermissionsModal({ isOpen, onClose, user }) {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (isOpen && user) {
      loadUserPermissions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user]);

  const loadUserPermissions = async () => {
    try {
      setLoading(true);
      const response = await rolePermissionService.getUserPermissions(user.id);
      setPermissions(response.payload?.permissions || []);
    } catch (error) {
      console.error('Error loading user permissions:', error);
      toast({
        title: 'Error',
        description: 'Error al cargar los permisos del usuario',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeColor = (roleName) => {
    switch (roleName) {
      case 'Admin': return 'purple';
      case 'Mecanico': return 'blue';
      case 'Recepcionista': return 'green';
      default: return 'gray';
    }
  };

  // Agrupar permisos por recurso
  const groupedPermissions = permissions.reduce((groups, permission) => {
    const resource = permission.resource;
    if (!groups[resource]) {
      groups[resource] = [];
    }
    groups[resource].push(permission);
    return groups;
  }, {});

  const resourceLabels = {
    users: 'Usuarios',
    roles: 'Roles',
    clients: 'Clientes',
    vehicles: 'Vehículos',
    services: 'Servicios',
    schedule: 'Agenda',
    reports: 'Reportes',
    inventory: 'Inventario',
    settings: 'Configuración',
    invoices: 'Facturas',
    providers: 'Proveedores',
    products: 'Productos'
  };

  const actionLabels = {
    view: 'Ver',
    create: 'Crear',
    edit: 'Editar',
    delete: 'Eliminar',
    assign: 'Asignar',
    view_own: 'Ver Propio',
    generate: 'Generar',
    manage: 'Gestionar'
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent maxW="700px">
        <ModalHeader>
          Permisos del Usuario
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody maxH="600px" overflowY="auto">
          <VStack spacing={4} align="stretch">
            {/* Información del usuario */}
            <HStack spacing={3} p={4} bg="gray.50" borderRadius="md">
              <Avatar size="md" name={user?.name} />
              <VStack align="start" spacing={1}>
                <Text fontWeight="bold" fontSize="lg">{user?.name}</Text>
                <Text fontSize="sm" color="gray.600">{user?.email}</Text>
                <HStack>
                  <Text fontSize="sm" color="gray.500">Rol:</Text>
                  {user?.role ? (
                    <Badge colorScheme={getRoleBadgeColor(user.role.name)}>
                      {user.role.name}
                    </Badge>
                  ) : (
                    <Badge colorScheme="red">Sin Rol</Badge>
                  )}
                </HStack>
              </VStack>
            </HStack>

            <Divider />

            {/* Permisos */}
            {loading ? (
              <Text>Cargando permisos...</Text>
            ) : permissions.length === 0 ? (
              <Box textAlign="center" py={8}>
                <Text color="gray.500" fontSize="lg">
                  Este usuario no tiene permisos asignados
                </Text>
                <Text color="gray.400" fontSize="sm" mt={2}>
                  Asigna un rol al usuario para otorgar permisos
                </Text>
              </Box>
            ) : (
              <VStack spacing={3} align="stretch">
                <HStack justify="space-between" align="center">
                  <Text fontSize="lg" fontWeight="semibold">
                    Permisos Asignados
                  </Text>
                  <Badge colorScheme="gray" fontSize="sm">
                    {permissions.length} permisos
                  </Badge>
                </HStack>

                <Accordion allowMultiple>
                  {Object.entries(groupedPermissions).map(([resource, resourcePermissions]) => (
                    <AccordionItem key={resource}>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <HStack>
                              <Text fontWeight="semibold">
                                {resourceLabels[resource] || resource}
                              </Text>
                              <Badge size="sm" colorScheme="gray">
                                {resourcePermissions.length}
                              </Badge>
                            </HStack>
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>
                      <AccordionPanel pb={4}>
                        <SimpleGrid columns={2} spacing={3}>
                          {resourcePermissions.map((permission) => (
                            <Box
                              key={permission.id}
                              p={3}
                              borderWidth={1}
                              borderRadius="md"
                              borderColor="gray.200"
                            >
                              <VStack align="start" spacing={1}>
                                <Text fontSize="sm" fontWeight="medium" color="blue.600">
                                  {actionLabels[permission.action] || permission.action}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  {permission.name}
                                </Text>
                                {permission.description && (
                                  <Text fontSize="xs" color="gray.400">
                                    {permission.description}
                                  </Text>
                                )}
                              </VStack>
                            </Box>
                          ))}
                        </SimpleGrid>
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Accordion>
              </VStack>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}